import { useEffect, useRef, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import { joiResolver } from '@hookform/resolvers/joi';
import cn from 'classnames';

import {
  useAPIIdentityDeveloperEmail,
  TAPIHookFetchCountriesResponse,
  useAPIFetchCountries,
  useAPIFetchStates,
  TAPIHookFetchStatesResponse,
  TStatesCode,
  TAPIHookFetchCountryDetailsResponse,
  useAPIFetchCountryDetails,
  ERROR_CODES,
} from '@marketplace/api';
import { emailRegEx, getAuthRedirectURL } from '@marketplace/common';
import { TextField, PasswordField, Dropdown, Checkbox, Button } from '@marketplace/ui-elements';
import { TERMS_OF_SERVICE, DEV_BEST_PRACTICES } from '@developer/constants';

import { RegistrationFormComplete, TRegistrationFormProps } from '../registration.types';
import { registrationFormValidationSchema } from './registration-form.validation';
import classes from './registration-form.module.scss';
import { DropdownChangeParams } from 'primereact/dropdown';
import { PRIVACY_POLICY } from 'src/app/shared/constants/routes';

export type TRegistrationFormData = {
  email: string;
  firstName: string;
  lastName: string;
  dial_country_id: any;
  phone: string;
  companyName: string;
  address: string;
  country: any;
  state: any;
  city: string;
  postalCode: string;
  agree: boolean;
};

export const RegistrationForm = (props: TRegistrationFormProps): JSX.Element => {
  const { formatMessage }: IntlShape = useIntl();
  const emailRef = useRef<HTMLFormElement>(null);
  const userNameRef = useRef<HTMLFormElement>(null);
  const { data: countries, loading: isFetchCountriesLoading }: TAPIHookFetchCountriesResponse = useAPIFetchCountries();
  const {
    data: states,
    loading: isFetchStatesLoading,
    fetchStates,
  }: TAPIHookFetchStatesResponse = useAPIFetchStates();
  const { handleSubmit, formState, watch, control, setError, clearErrors, getValues, setValue} =
    useForm<TRegistrationFormData>({
      mode: 'onBlur',
      defaultValues: props.initialValues,
      shouldUnregister: true,
      resolver: joiResolver(registrationFormValidationSchema({ formatMessage, countries })),
    });
  const countryFieldValue = useWatch({ name: 'country', control });
  const { data: countryDetails, loading: isFetchCountryDetailsLoading }: TAPIHookFetchCountryDetailsResponse = useAPIFetchCountryDetails();
  const [isd_code, setIsd_code] = useState('');
  const [isEmailExistsInGIS, setIsEmailExistsInGIS] = useState(false);

  const hasStates = useMemo(() => {
    const countryFieldKey = getValues('country')?.key;
    for (const key of Object.values(TStatesCode)) {
      if (TStatesCode[key] === countryFieldKey) {
        return true;
      }
    }
    return false;
  }, [countryFieldValue]);

  /* Store Form Data On Change
   * Preventing losing data while switching between steps without clicking to submit button */
  useEffect(() => {
    const subscription = watch((values: { [key: string]: any }, { name }: any) => {
      props.onChange(RegistrationFormComplete.REGISTRATION_FORM, { [name]: values[name] });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    props.onChange(RegistrationFormComplete.REGISTRATION_FORM, { isValid: formState.isValid });
  }, [formState.isValid]);

  const handleCountry = (e: DropdownChangeParams) => {
    const countryFieldKey = e.value?.key;
    setValue('country', e.value, { shouldDirty: true, shouldValidate: true });
    for (const key of Object.values(TStatesCode)) {
      if (TStatesCode[key] === countryFieldKey) {
        fetchStates(TStatesCode[key]);
        break;
      }
    }
  };

  const handleCountryForDialCode = (e: DropdownChangeParams) => {
    setValue('dial_country_id', e.value, { shouldDirty: true, shouldValidate: true });
    setIsd_code(e.value.isd_code);
  };

  const onFormClear = (e: { preventDefault: () => void }) => {
    setValue('email', '', { shouldDirty: true });
    setValue('firstName', '', { shouldDirty: true });
    setValue('lastName', '', { shouldDirty: true });
    setValue('dial_country_id', undefined, { shouldDirty: true });
    setValue('phone', '', { shouldDirty: true });
    setValue('companyName', '', { shouldDirty: true });
    setValue('address', '', { shouldDirty: true });
    setValue('country', undefined, { shouldDirty: true});
    setValue('state', {}, { shouldDirty: true });
    setValue('city', '', { shouldDirty: true });
    setValue('postalCode', '', { shouldDirty: true });
    setValue('agree', false, { shouldDirty: true });
    setIsd_code('');
    clearErrors();
    setIsEmailExistsInGIS(false);
    e.preventDefault();
  };

  const getFHIRSupportURL=()=>{
    return "https://developers.greenwayhealth.com/developer-platform/docs/fhir-support"
  }

  const handleSubmitForm = async (formData: any) => {
    setIsEmailExistsInGIS(false);
    const emailLabel = formatMessage({ id: 'registrationContactInformation_email' });

    if(props.submitResponseData?.errors){

      if (props.submitResponseData?.errors[0]?.code === ERROR_CODES.EMAIL_ALREADY_EXTSTS_IN_BOTH_GIS_AND_DP_DATABASE) 
      {
        setError('email', {
          message: formatMessage({ id: 'emailExistsInBothGISAndDPDatabaseMessage' }, { field: emailLabel }),
        });
        setIsEmailExistsInGIS(true);
        return;
      }

      if ( props.submitResponseData?.errors[0]?.code === ERROR_CODES.EMAIL_ALREADY_EXTSTS_IN_GIS_DATABASE_NOT_IN_DP_DATABASE)
      {
        setError('email', {
          message: formatMessage({ id: 'emailExistsInGISDatabaseNotInDPMessage' }, { field: emailLabel }),
        });
        setIsEmailExistsInGIS(true);
        return;
      }

      if (props.submitResponseData?.errors[0]?.code === ERROR_CODES.EMAIL_ALREADY_EXTSTS_IN_DP_DATABASE_NOT_IN_GIS_DATABASE) 
      {
        clearErrors('email');
        setIsEmailExistsInGIS(false);
        return;
      }
  }

  if(formState?.errors?.email?.message){
    setIsEmailExistsInGIS(false);
    return;
  }

    props.onSubmit(formData);
  };

  const emailError = isEmailExistsInGIS ? (
    <span>
      {formState.errors.email?.message} Please contact us <a href={getFHIRSupportURL()} target='_blank' rel="noreferrer"> here.</a>
    </span>
  ) : (
    formState?.errors?.email?.message
  );

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)} ref={props.formRef} id="registrationForm">
      <div className={classes.row}>
        <h1 className={classes.formheader}>{formatMessage({ id: 'registrationFormHeader' })}</h1>
      </div>
      <div className={cn(classes.privacy)}>
        {formatMessage(
          { id: 'registrationFormDataCollection1' },
          {
            privacy_policy: (
              <a href={PRIVACY_POLICY} target="_blank" rel="noreferrer">
                {formatMessage({ id: 'registrationFormDataCollection2' })}
              </a>
            ),
          }
        )}
      </div>
      <div className={classes.row}>
        <TextField
          isRequired
          //autoFocus
          maxLength={100}
          control={control}
          error={emailError}
          placeholder={formatMessage(
            { id: 'generalForm_placeholder_select' },
            { field: formatMessage({ id: 'registrationContactInformation_email' }) }
          )}
          innerRef={emailRef}
          rootClassName={classes.emailField}
          wrapperInputClassName={classes.wrapperEmailFieldClassName}
          inputClassName={classes.emailInput}
          label={formatMessage({ id: 'registrationContactInformation_email' })}
          onChange={(e)=>
            {
              setValue('email', e.target.value, { shouldDirty: true, shouldValidate: true });
              setIsEmailExistsInGIS(false)}}
          fieldId="email"
          name="email"
          type="email"
        />
      </div>
      <div className={classes.loginLink}>
        {formatMessage(
          { id: 'registrationModalFeature_account' },
          {
            login: <a href={getAuthRedirectURL()}>{formatMessage({ id: 'registrationModalFeature_login' })}</a>,
          }
        )}
      </div>
      <div className={classes.row}>
        <TextField
          isRequired
          maxLength={100}
          control={control}
          error={formState.errors.firstName?.message}
          placeholder={formatMessage(
            { id: 'generalForm_placeholder_select' },
            { field: formatMessage({ id: 'registrationContactInformation_firstName' }) }
          )}
          rootClassName={classes.firstNameField}
          inputClassName={classes.firstNameInput}
          label={formatMessage({ id: 'registrationContactInformation_firstName' })}
          fieldId="firstName"
          name="firstName"
        />
        <TextField
          isRequired
          maxLength={100}
          control={control}
          error={formState.errors.lastName?.message}
          placeholder={formatMessage(
            { id: 'generalForm_placeholder_select' },
            { field: formatMessage({ id: 'registrationContactInformation_lastName' }) }
          )}
          rootClassName={classes.lastNameField}
          inputClassName={classes.lastNameInput}
          label={formatMessage({ id: 'registrationContactInformation_lastName' })}
          fieldId="lastName"
          name="lastName"
        />
      </div>

      <div className={classes.customRow}>
        <div>
          <Dropdown
            isRequired
            filter
            filterBy="country_name"
            options={countryDetails}
            onChange={handleCountryForDialCode}
            control={control}
            error={formState.errors.dial_country_id?.message}
            isLoading={isFetchCountryDetailsLoading}
            placeholder={formatMessage(
              { id: 'generalForm_placeholder_select' },
              { field: formatMessage({ id: 'registrationLocation_country' }) }
            )}
            label={formatMessage({ id: 'registrationContactInformation_phone' })}
            rootClassName={classes.countryField}
            fieldId="dial_country_id"
            optionLabel="country_name"
            name="dial_country_id"
            labelClassName={classes.dropDownLabel}
            id='registrationLocation_country'
          />
        </div>
        <div className={classes.isd_wrapper}>
          <div className={classes.isd_container}>{isd_code}</div>
        </div>
        <div>
          <TextField
            control={control}
            error={formState.errors.phone?.message}
            maxLength={10}
            placeholder={formatMessage(
              { id: 'registrationMobilePlaceholder' },
              { field: formatMessage({ id: 'registrationContactInformation_phone' }) }
            )}
            rootClassName={classes.phoneField}
            inputClassName={classes.phoneInput}
            type="tel"
            fieldId="phone"
            name="phone"
          />
        </div>
      </div>

      <div className={classes.row}>
        <TextField
          maxLength={100}
          control={control}
          error={formState.errors.companyName?.message}
          placeholder={formatMessage(
            { id: 'generalForm_placeholder_select' },
            { field: formatMessage({ id: 'registrationLocation_companyName' }) }
          )}
          rootClassName={classes.companyNameField}
          label={formatMessage({ id: 'registrationLocation_companyName' })}
          fieldId="companyName"
          name="companyName"
        />
      </div>

      <div className={classes.row}>
        <Dropdown
          isRequired
          filter
          filterBy="country"
          options={countries}
          onChange={handleCountry}
          control={control}
          error={formState.errors.country?.message}
          isLoading={isFetchCountriesLoading}
          placeholder={formatMessage(
            { id: 'generalForm_placeholder_select' },
            { field: formatMessage({ id: 'registrationLocation_country' }) }
          )}
          rootClassName={classes.countryField}
          label={formatMessage({ id: 'registrationLocation_country' })}
          fieldId="country"
          optionLabel="country"
          name="country"
          labelClassName={classes.dropDownLabel}
          data-testId="country"
        />
        {!hasStates ? null : (
          <Dropdown
            filter={true}
            isRequired={true}
            options={states}
            control={control}
            optionLabel="state"
            filterBy="state"
            isLoading={isFetchStatesLoading}
            error={formState.errors.state?.message}
            placeholder={formatMessage(
              { id: 'generalForm_placeholder_select' },
              { field: formatMessage({ id: 'registrationLocation_state' }) }
            )}
            rootClassName={classes.stateField}
            label={formatMessage({ id: 'registrationLocation_state' })}
            fieldId="state"
            name="state"
            labelClassName={classes.dropDownLabel}
          />
        )}
      </div>
      <div className={classes.row}>
        <TextField
          isRequired
          maxLength={100}
          control={control}
          error={formState.errors.city?.message}
          placeholder={formatMessage(
            { id: 'generalForm_placeholder_select' },
            { field: formatMessage({ id: 'registrationLocation_city' }) }
          )}
          rootClassName={classes.cityField}
          label={formatMessage({ id: 'registrationLocation_city' })}
          fieldId="city"
          name="city"
        />
        <TextField
          maxLength={20}
          control={control}
          error={formState.errors.postalCode?.message}
          placeholder={formatMessage(
            { id: 'generalForm_placeholder_select' },
            { field: formatMessage({ id: 'registrationLocation_postalCode' }) }
          )}
          rootClassName={classes.postalCodeField}
          label={formatMessage({ id: 'registrationLocation_postalCode' })}
          fieldId="postalCode"
          name="postalCode"
        />
      </div>
      <div>
        <TextField
          maxLength={2000}
          control={control}
          error={formState.errors.address?.message}
          placeholder={formatMessage(
            { id: 'generalForm_placeholder_select' },
            { field: formatMessage({ id: 'registrationLocation_address' }) }
          )}
          rootClassName={classes.address}
          label={formatMessage({ id: 'registrationLocation_address' })}
          fieldId="address"
          name="address"
        />
      </div>
      <div className={cn(classes.agree)}>
        {formatMessage(
          { id: 'registrationLocation_term1' },
          {
            terms: (
              <a href={TERMS_OF_SERVICE} target="_blank" rel="noreferrer">
                {formatMessage({ id: 'registrationLocation_term2' })}
              </a>
            ),
            code: (
              <a href={DEV_BEST_PRACTICES} target="_blank" rel="noreferrer">
                {formatMessage({ id: 'registrationLocation_term3' })}
              </a>
            ),
          }
        )}
      </div>
      <div className={classes.row}>
        <Checkbox
          control={control}
          error={formState.errors.agree?.message}
          rootClassName={classes.agreeCheckbox}
          label={formatMessage({ id: 'registrationLocation_agree' })}
          fieldId="agree"
          name="agree"
        />
      </div>

      {/* The button below allow submit form on Enter */}
      <button hidden tabIndex={-1} />

      <div className={classes.buttonFooter}>
        <div className={classes.footerRight}>
          <Button variant="secondary" onClick={onFormClear} id="registration-form-clear-button">
            {props.clearLabel}
          </Button>
          <Button
            id="registration-form-submit-button"
            loading={props.isLoading}
            disabled={props.isAcceptDisable}
            rootClassName={classes.accept}
            onClick={props.onAccept}
          >
            {props.acceptLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};
